import {
  PortalTaskFragment,
  usePortalDailyReportSubmitMutation,
  usePortalMyWorkQuery,
  usePortalTaskCompleteMutation,
} from "@dashboard/graphql";
import { Box, Button, Text, Textarea } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { ErrorText, InlineText, Page, Section } from "../components";
import { portalMessages as m } from "../messages";
import { TASK_STATUS, useTaskStatusLabel } from "../taskLabels";
import { formatDateTime, formatDay, today } from "../utils";

const TaskRow = ({
  task,
  onChanged,
  canComplete,
}: {
  task: PortalTaskFragment;
  onChanged: () => void;
  canComplete: boolean;
}) => {
  const intl = useIntl();
  const statusLabel = useTaskStatusLabel();
  const [open, setOpen] = useState(false);
  const [complete, opts] = usePortalTaskCompleteMutation({
    onCompleted: data => {
      if (!data.taskComplete?.errors.length) {
        setOpen(false);
        onChanged();
      }
    },
  });
  const late = task.status === TASK_STATUS.OPEN && new Date(task.dueAt) < new Date();

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      paddingY={2}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderColor="default1"
    >
      <Box display="flex" gap={2} alignItems="baseline" flexWrap="wrap">
        <Text size={3} fontWeight="bold">
          {task.title}
        </Text>
        <Text size={2} color={late ? "critical1" : "default2"}>
          {intl.formatMessage(m.dueAt, { when: formatDateTime(task.dueAt) })}
        </Text>
        <Text size={2} color="default2">
          · {statusLabel(task.status)} · {intl.formatMessage(m.by, { name: task.assignedByName })}
        </Text>
        <Box __flex={1} />
        {canComplete && task.status === TASK_STATUS.OPEN && !open && (
          <Button size="small" variant="primary" onClick={() => setOpen(true)}>
            {intl.formatMessage(m.markDone)}
          </Button>
        )}
      </Box>
      {task.description && (
        <Text size={2} __whiteSpace="pre-wrap">
          {task.description}
        </Text>
      )}
      {task.reviewNote && task.status === TASK_STATUS.OPEN && (
        <Text size={2} color="warning1">
          {intl.formatMessage(m.returnedNote, { note: task.reviewNote })}
        </Text>
      )}
      {open && (
        <InlineText
          placeholder={intl.formatMessage(m.doneNotePlaceholder)}
          loading={opts.loading}
          onCancel={() => setOpen(false)}
          onSubmit={note => complete({ variables: { id: task.id, note } })}
        />
      )}
      <ErrorText errors={opts.data?.taskComplete?.errors} />
    </Box>
  );
};

const ReportForm = ({
  day,
  initial,
  onDone,
}: {
  day: string;
  initial: string;
  onDone: () => void;
}) => {
  const intl = useIntl();
  const [text, setText] = useState(initial);
  const [submit, opts] = usePortalDailyReportSubmitMutation({
    onCompleted: data => {
      if (!data.dailyReportSubmit?.errors.length) {
        onDone();
      }
    },
  });

  useEffect(() => setText(initial), [initial]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Textarea
        rows={5}
        maxLength={8000}
        label={intl.formatMessage(m.reportFor, { day: formatDay(day) })}
        value={text}
        onChange={event => setText(event.target.value)}
      />
      <Box>
        <Button
          size="small"
          variant="primary"
          disabled={!text.trim() || opts.loading}
          onClick={() => submit({ variables: { date: day, text: text.trim() } })}
        >
          {intl.formatMessage(m.send)}
        </Button>
      </Box>
      <ErrorText errors={opts.data?.dailyReportSubmit?.errors} />
    </Box>
  );
};

export const Tasks = () => {
  const intl = useIntl();
  const statusLabel = useTaskStatusLabel();
  const { data, refetch } = usePortalMyWorkQuery({ fetchPolicy: "cache-and-network" });
  const settings = data?.myWorkSettings;
  const reports = data?.myDailyReports ?? [];
  const todayReport = reports.find(r => r.date === today());
  const missing = (data?.myMissingReports ?? []).filter(d => d !== today());
  const closed = (data?.closed ?? []).filter(
    t => t.status !== TASK_STATUS.OPEN && t.status !== TASK_STATUS.DONE,
  );

  return (
    <Page>
      {settings?.tasksEnabled && (
        <Section title={intl.formatMessage(m.myTasks)}>
          {(data?.myTasks ?? []).length === 0 && (
            <Text size={2} color="default2">
              {intl.formatMessage(m.noTasks)}
            </Text>
          )}
          {data?.myTasks.map(task => (
            <TaskRow key={task.id} task={task} onChanged={refetch} canComplete />
          ))}
        </Section>
      )}
      <Section title={intl.formatMessage(m.dailyReport)}>
        <Text size={1} color="default2">
          {intl.formatMessage(
            settings?.dailyReportRequired ? m.reportRequiredHint : m.reportOptionalHint,
          )}
        </Text>
        {todayReport?.status === "approved" ? (
          <Text size={2}>{todayReport.text}</Text>
        ) : (
          <ReportForm day={today()} initial={todayReport?.text ?? ""} onDone={refetch} />
        )}
        {todayReport?.status === "returned" && (
          <Text size={2} color="warning1">
            {intl.formatMessage(m.returnedNote, { note: todayReport.reviewNote })}
          </Text>
        )}
        {missing.map(day => (
          <ReportForm key={day} day={day} initial="" onDone={refetch} />
        ))}
      </Section>
      {(closed.length > 0 || reports.length > 0) && (
        <Section title={intl.formatMessage(m.history)}>
          {closed.slice(0, 20).map(task => (
            <Text key={task.id} size={2}>
              {formatDateTime(task.dueAt)} · {task.title} — {statusLabel(task.status)}
            </Text>
          ))}
          {reports.slice(0, 20).map(report => (
            <Text key={report.id} size={2} color="default2">
              {formatDay(report.date)} · {intl.formatMessage(m.dailyReport)} —{" "}
              {statusLabel(report.status)}
              {report.reviewNote && ` (${report.reviewNote})`}
            </Text>
          ))}
        </Section>
      )}
    </Page>
  );
};
